import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Globe, Mail, Phone, Linkedin, MapPin, Plus, Pencil, StickyNote,
  Users, Target, CheckSquare, Clock, Building2, Trash2,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { CommittenteBadge } from "../components/CommittenteSelect";
import Timeline from "../components/Timeline";
import FormDialog from "../components/FormDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { COMPANY_FIELDS, CONTACT_FIELDS, OPPORTUNITY_FIELDS, ACTIVITY_FIELDS } from "../lib/entities";
import { fmtEUR, fmtDate, fmtDateTime, initials, label } from "../lib/format";

export default function CompanyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/companies/${id}`);
    setC(data);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post("/notes", { company_id: id, content: note });
    setNote(""); toast.success("Nota aggiunta"); load();
  };

  if (!c) return <div className="text-slate-400 py-10">Caricamento…</div>;

  const quickActions = [
    { label: "Attività", fields: ACTIVITY_FIELDS, endpoint: "/activities", title: "Nuova Attività" },
    { label: "Opportunità", fields: OPPORTUNITY_FIELDS, endpoint: "/opportunities", title: "Nuova Opportunità" },
    { label: "Contatto", fields: CONTACT_FIELDS, endpoint: "/contacts", title: "Nuovo Contatto" },
    { label: "Follow-up", fields: ACTIVITY_FIELDS, endpoint: "/activities", title: "Nuovo Follow-up", init: { type: "follow-up" } },
    { label: "Meeting", fields: ACTIVITY_FIELDS, endpoint: "/activities", title: "Nuovo Meeting", init: { type: "meeting" } },
  ];

  const Info = ({ icon: Icon, label: l, value, href }) => value ? (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="w-4 h-4 text-slate-400 shrink-0" />
      {href ? <a href={href} target="_blank" rel="noreferrer" className="text-teal-600 hover:underline truncate">{value}</a> : <span className="text-slate-600 truncate">{value}</span>}
    </div>
  ) : null;

  return (
    <div>
      <button onClick={() => nav("/aziende")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Aziende</button>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 mb-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
            <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-xl bg-navy-900 text-white flex items-center justify-center font-heading font-bold text-lg sm:text-xl">{initials(c.legal_name)}</div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h1 className="text-xl sm:text-2xl font-bold font-heading text-slate-900 break-words">{c.legal_name}</h1>
                <StatusBadge group="classification" value={c.classification} />
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{c.sector || "—"} · {c.country || "—"} {c.rating && `· Rating ${c.rating}`}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(c.tags || []).map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-mono">{t}</span>)}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {can("write") && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button data-testid="company-quick-actions" className="bg-teal-600 hover:bg-teal-700 text-white gap-1.5"><Plus className="w-4 h-4" /> Azione rapida</Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {quickActions.map((qa) => (
                    <DropdownMenuItem key={qa.label} data-testid={`qa-${qa.label.toLowerCase()}`} onClick={() => setDialog(qa)} className="cursor-pointer">+ {qa.label}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {can("write") && <Button variant="outline" data-testid="edit-company-detail" onClick={() => setEdit(true)} className="gap-1.5"><Pencil className="w-4 h-4" /> Modifica</Button>}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5 pt-5 border-t border-slate-100 min-w-0">
          <Info icon={Globe} value={c.website} href={c.website ? `https://${c.website.replace(/^https?:\/\//, "")}` : null} />
          <Info icon={Mail} value={c.email} href={`mailto:${c.email}`} />
          <Info icon={Phone} value={c.phone} />
          <Info icon={MapPin} value={[c.city, c.region, c.country].filter(Boolean).join(", ")} />
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto justify-start w-full sm:w-auto">
          {["overview", "contatti", "lead", "opportunita", "attivita", "progetti", "note", "timeline"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`entity-tab-${t}`} className="capitalize">{t === "opportunita" ? "Opportunità" : t === "attivita" ? "Attività" : t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat icon={Users} label="Contatti" value={c.contacts.length} />
            <Stat icon={Target} label="Opportunità" value={c.opportunities.length} sub={fmtEUR(c.opportunities.reduce((s, o) => s + (o.value || 0), 0))} />
            <Stat icon={CheckSquare} label="Attività" value={c.activities.length} />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 mt-3">
            <p className="text-sm font-heading font-semibold mb-3">Dettagli</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2.5 gap-x-6 text-sm">
              {[["Nome commerciale", c.trade_name], ["Committente", c.committente ? <CommittenteBadge value={c.committente} testid="company-detail-committente" /> : null], ["Settore", c.sector], ["Fatturato", c.revenue_band], ["Codice ATECO", c.ateco ? `${c.ateco}${c.ateco_label ? ` · ${c.ateco_label}` : ""}` : null], ["Sottosettore", c.subsector], ["Dipendenti", c.employees], ["Mercati export", (c.export_markets || []).join(", ")], ["Fonte", c.source], ["Owner", c.owner?.name]].map(([k, v]) => (
                <div key={k}><p className="text-xs text-slate-400">{k}</p><p className="text-slate-700">{v || "—"}</p></div>
              ))}
            </div>
            {c.notes && typeof c.notes === "string" && <p className="text-sm text-slate-600 mt-4 pt-4 border-t border-slate-100">{c.notes}</p>}
          </div>
        </TabsContent>

        <TabsContent value="contatti" className="mt-4">
          <RelList items={c.contacts} empty="Nessun contatto" render={(p) => (
            <button key={p.id} onClick={() => nav(`/contatti/${p.id}`)} className="w-full text-left flex items-center gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50">
              <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center text-teal-700 text-xs font-semibold">{initials(`${p.first_name} ${p.last_name}`)}</div>
              <div className="flex-1"><p className="font-medium text-slate-800 text-sm">{p.first_name} {p.last_name}</p><p className="text-xs text-slate-400">{p.job_title}</p></div>
              <StatusBadge group="contact_status" value={p.relationship_status} />
            </button>
          )} />
        </TabsContent>

        <TabsContent value="lead" className="mt-4">
          <RelList items={c.leads || []} empty="Nessun lead" render={(l) => (
            <div key={l.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-50">
              <div className="flex-1"><p className="font-medium text-slate-800 text-sm">{l.title}</p><p className="text-xs text-slate-400">{l.service || "—"} · {l.source || "—"}</p></div>
              <span className="font-mono text-xs text-teal-700">Score {l.score ?? "—"}</span>
              <StatusBadge group="lead_status" value={l.status} />
            </div>
          )} />
        </TabsContent>

        <TabsContent value="opportunita" className="mt-4">
          <RelList items={c.opportunities} empty="Nessuna opportunità" render={(o) => (
            <div key={o.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-50">
              <div className="flex-1"><p className="font-medium text-slate-800 text-sm">{o.name}</p><p className="text-xs text-slate-400">{o.service || "—"}</p></div>
              <span className="font-mono text-sm text-slate-700">{fmtEUR(o.value)}</span>
              <StatusBadge group="opp_status" value={o.status} />
            </div>
          )} />
        </TabsContent>

        <TabsContent value="attivita" className="mt-4">
          <RelList items={c.activities} empty="Nessuna attività" render={(a) => (
            <div key={a.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-50">
              <div className="flex-1"><p className="font-medium text-slate-800 text-sm">{a.subject}</p><p className="text-xs text-slate-400">{label("activity_type", a.type)} · {fmtDateTime(a.due_date)}</p></div>
              <StatusBadge group="activity_status" value={a.status} />
            </div>
          )} />
        </TabsContent>

        <TabsContent value="progetti" className="mt-4">
          <RelList items={c.projects || []} empty="Nessun progetto" render={(p) => (
            <div key={p.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-50">
              <div className="flex-1"><p className="font-medium text-slate-800 text-sm">{p.name}</p><p className="text-xs text-slate-400">{p.service || "—"} · {fmtDate(p.start_date)} → {fmtDate(p.end_date)}</p></div>
              <span className="font-mono text-sm text-slate-700">{fmtEUR(p.value)}</span>
              <StatusBadge group="project_status" value={p.status} />
            </div>
          )} />
        </TabsContent>

        <TabsContent value="note" className="mt-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            {can("write") && (
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Aggiungi una nota…" rows={2} />
                <Button data-testid="add-note-btn" onClick={addNote} className="bg-navy-900 hover:bg-navy-700 text-white sm:self-end min-h-[44px] sm:min-h-0">Aggiungi</Button>
              </div>
            )}
            <div className="space-y-2">
              {(c.notes_list || []).map((n) => (
                <div key={n.id} className="flex items-start gap-2.5 p-3 rounded-lg bg-slate-50">
                  <StickyNote className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div className="flex-1"><p className="text-sm text-slate-700">{n.content}</p><p className="text-xs text-slate-400 mt-1 font-mono">{fmtDateTime(n.created_at)} · {n.created_by === c.owner?.id ? c.owner?.name : ""}</p></div>
                </div>
              ))}
              {(c.notes_list || []).length === 0 && <p className="text-sm text-slate-400 text-center py-6">Nessuna nota</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5"><Timeline events={c.timeline} /></div>
        </TabsContent>
      </Tabs>

      {edit && <FormDialog open={edit} onOpenChange={setEdit} title="Modifica Azienda" fields={COMPANY_FIELDS} initial={c} endpoint={`/companies/${id}`} method="put" onSaved={load} />}
      {dialog && <FormDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)} title={dialog.title} fields={dialog.fields} endpoint={dialog.endpoint} initial={{ company_id: id, ...(dialog.init || {}) }} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center"><Icon className="w-5 h-5 text-teal-600" /></div>
      <div><p className="text-xl font-bold font-heading text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}{sub ? ` · ${sub}` : ""}</p></div>
    </div>
  );
}

function RelList({ items, render, empty }) {
  return <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">{items.length ? items.map(render) : <p className="text-sm text-slate-400 text-center py-10">{empty}</p>}</div>;
}
